import { Fragment, useState } from 'react';
import Toast from 'react-bootstrap/Toast';
import { postFeedback } from '../../services/apiClient';
import { getStoredLoggedUser } from '../../utils/authStorage';
import { markFeedbackAnswered } from '../../utils/cookies';

/**
 * Port of `utilComponents/feedback.jsx` — the satisfaction prompt.
 *
 * Behavior kept as-is, including two things worth naming:
 *  - Choosing "Take me back to previous version" submits immediately rather than waiting
 *    for the Submit button (feedback.jsx:17-23). That radio is commented out in the
 *    original's markup, so the branch is unreachable today; it is carried because
 *    re-enabling the radio should restore the original behavior, not a changed one.
 *  - The submission is fire-and-forget: the original never awaited it and never showed a
 *    failure. Preserved — a failed feedback post must not block the user or raise a
 *    modal over their timesheet.
 *
 * The `user_id` argument the original passed to sendFeedback was never used by it; the
 * identity that reaches the service is `feedbackBy` in the body, from localStorage.
 */
export function Feedback({
  hide,
  feedbackVisible,
}: {
  hide: () => void;
  feedbackVisible: (visible: boolean) => void;
}): JSX.Element {
  const [selectedOption, setSelectedOption] = useState('');

  const submit = (option: string): void => {
    const trimmed = option.trim();
    if (trimmed.length === 0) return;

    void postFeedback({
      applicationCode: 'Checkin-Web-Revamp',
      functionality: 'Time-Entry',
      ratings: 0,
      feedbackBy: getStoredLoggedUser()?.userName,
      ratingsCategory: trimmed,
      ratingsComment: trimmed,
      // Swallowed deliberately — see the note above.
    }).catch(() => undefined);

    markFeedbackAnswered();
    hide();
    feedbackVisible(false);
  };

  const onValueChange = (value: string): void => {
    setSelectedOption(value);
    if (value === 'Take me back to previous version') {
      submit(value);
    }
  };

  const options = [
    { id: 'inlineRadio1', label: 'Very Satisfied' },
    { id: 'inlineRadio2', label: 'Satisfied' },
    { id: 'inlineRadio3', label: 'Neutral' },
    { id: 'inlineRadio4', label: 'Unsatisfied' },
  ];

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 20,
        right: 10,
        zIndex: 9999,
        float: 'right',
      }}
    >
      <Toast
        className="feedback"
        onClose={() => {
          hide();
          feedbackVisible(false);
        }}
      >
        <Toast.Header closeButton={true}>
          <img src="" className="rounded me-2" alt="" />
          <strong className="me-auto">
            How would you rate your experience with the Improved Version of CheckIn?
          </strong>
        </Toast.Header>
        <Toast.Body>
          <div>
            {/* A Fragment, not a wrapper div: `.form-check-inline` is inline-block and
                the <br/> between entries is what stacks them. An extra block-level
                wrapper would change the layout. The original has a <br/> BETWEEN the
                radios and none after the last one — reproduced by the index guard. */}
            {options.map((option, index) => (
              <Fragment key={option.id}>
                <div className="form-check form-check-inline">
                  <input
                    className="form-check-input"
                    type="radio"
                    id={option.id}
                    value={option.label}
                    checked={selectedOption === option.label}
                    onChange={(event) => {
                      onValueChange(event.target.value);
                    }}
                  />
                  <label className="form-check-label" htmlFor={option.id}>
                    {option.label}
                  </label>
                </div>
                {index < options.length - 1 && <br />}
              </Fragment>
            ))}
          </div>
          <div>
            <hr></hr>
          </div>
          <button
            onClick={() => {
              submit(selectedOption);
            }}
            className="btn modal-button"
          >
            {' '}
            Submit
          </button>
        </Toast.Body>
      </Toast>
    </div>
  );
}
